import React, { useEffect, useState, useMemo, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFormStep } from "@store/ui/formStepSlice";
import "@styles/pages/Inquiry/step3/WorkshopChangePage.scss";
import {
  Box,
  MenuItem,
  Select,
  Typography,
  TextField,
  useMediaQuery,
  Checkbox,
  Button,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Trans, useTranslation } from "react-i18next";
import { useForm, Controller, useWatch, set } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { WorkshopChangeValidationSchema } from "@models/ValidationSchema";
import { modifyWorkshopEnrollment } from "@api/enrollmentApi";
import { toast } from "react-toastify";
import Popup from "reactjs-popup";

const WorkshopChangePage = () => {
  const { t, i18n } = useTranslation();

  const dispatch = useDispatch();
  const inquiryLoginData = useSelector(
    (state) => state.inquiry.inquiryLoginData
  );
  const userWorkshopId = useSelector((state) => state.inquiry.userWorkshopId);
  const workshopDataById = useSelector(
    (state) => state.inquiry.workshopDataById
  );
  const isChinese = i18n.language === "hk";
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    resetField,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {},
    resolver: yupResolver(WorkshopChangeValidationSchema),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [workshopData, setWorkshopData] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);

  useEffect(() => {
    console.log(workshopDataById);
    setWorkshopData(workshopDataById);
    reset({ event_name: workshopDataById[0].id });
  }, []);

  const handleUpdateClick = () => {
    setOpenPopup(true);
    console.log(getValues("event_time"));
    console.log(getValues("event_date"));
  };

  const modifyWorkshop = async () => {
    const finalData = {
      id: userWorkshopId,
      email: inquiryLoginData.email,
      phone: inquiryLoginData.select + inquiryLoginData.phone,
      workshop_detail_id: getValues("event_time"),
      workshop_date: getValues("event_date"),
    };
    setIsLoading(true);
    try {
      const res = await modifyWorkshopEnrollment(finalData);
      console.log(res);
      const statusCode = res.data.StatusCode;
      if (statusCode === 200) {
        toast.success(t("inquiry:update_success"));
        dispatch(setFormStep({ formKey: "inquiry", step: 1 }));
      }
    } catch (err) {
      console.error(err);
      toast.error(t("inquiry:fetch_error"));
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Box className="workshop-change-container">
      <Typography className="title">
        <Trans i18nKey="inquiry:course_inquiry" />
      </Typography>
      {/* Event Name */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="form:event_name" />
        </Typography>
        <Controller
          name="event_name"
          control={control}
          render={({ field }) => {
            return (
              <Select
                sx={{ width: "100%", textAlign: "center" }}
                {...field}
                value={field.value || ""}
                error={!!errors.event_name}
                disabled={true}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  resetField("event_location");
                  resetField("event_date");
                  resetField("event_time");
                }}
              >
                {workshopData.map((item) => (
                  <MenuItem
                    key={item.id}
                    value={item.id}
                    sx={{ display: "block", textAlign: "center" }}
                  >
                    {isChinese ? item.title_hk : item.title_hk_en}
                  </MenuItem>
                ))}
              </Select>
            );
          }}
        />
        {errors.event_name && (
          <Typography className="error-text">
            <Trans i18nKey="form:event_name_error" />
          </Typography>
        )}
      </Box>
      {/* Event Location */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="form:event_location" />
        </Typography>
        <Controller
          name="event_location"
          control={control}
          render={({ field }) => {
            const event = workshopData.find(
              (e) => e.id === getValues("event_name")
            );
            const locations = event?.workshop_location || [];

            return (
              <Select
                sx={{ width: "100%", textAlign: "center" }}
                {...field}
                disabled={!event}
                error={!!errors.event_location}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  resetField("event_date");
                  resetField("event_time");
                }}
              >
                {locations.map((loc, index) => (
                  <MenuItem
                    key={index}
                    value={loc.address_hk_en}
                    sx={{ display: "block", textAlign: "center" }}
                  >
                    {isChinese ? loc.address_hk : loc.address_hk_en}
                  </MenuItem>
                ))}
              </Select>
            );
          }}
        />
        {errors.event_location && (
          <Typography className="error-text">
            <Trans i18nKey="form:event_location_error" />
          </Typography>
        )}
      </Box>{" "}
      {/* Event Date */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="form:event_date" />
        </Typography>
        <Controller
          name="event_date"
          control={control}
          render={({ field }) => {
            const event = workshopData.find(
              (e) => e.id === getValues("event_name")
            );
            const location = event?.workshop_location?.find(
              (l) => l.address_hk_en === getValues("event_location")
            );
            const dates = location?.workshop_date || [];

            return (
              <>
                <Select
                  sx={{ width: "100%", textAlign: "center" }}
                  {...field}
                  value={field.value || ""}
                  disabled={!location}
                  error={!!errors.event_date}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    resetField("event_time", "");
                  }}
                >
                  {dates.map((item) => (
                    <MenuItem
                      key={item.date}
                      value={item.date}
                      sx={{ display: "block", textAlign: "center" }}
                    >
                      {item.date}
                    </MenuItem>
                  ))}
                </Select>{" "}
                {errors.event_date && (
                  <Typography className="error-text">
                    <Trans i18nKey="form:evnet_date_error" />
                  </Typography>
                )}
              </>
            );
          }}
        />
      </Box>
      {/* Event Time */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="form:event_time" />
        </Typography>
        <Controller
          name="event_time"
          control={control}
          render={({ field }) => {
            const event = workshopData.find(
              (e) => e.id === getValues("event_name")
            );
            const location = event?.workshop_location?.find(
              (l) => l.address_hk_en === getValues("event_location")
            );
            const dateObj = location?.workshop_date?.find(
              (d) => d.date === getValues("event_date")
            );
            const timeList = dateObj?.workshop_time || [];

            return (
              <>
                <Select
                  sx={{ width: "100%", textAlign: "center" }}
                  {...field}
                  value={field.value || ""}
                  disabled={!dateObj}
                  error={!!errors.event_time}
                  onChange={(e) => {
                    field.onChange(parseInt(e.target.value, 10)); // store only ID
                  }}
                >
                  {timeList.map((item) => (
                    <MenuItem
                      key={item.workshop_detail_id}
                      value={item.workshop_detail_id}
                      sx={{ display: "block", textAlign: "center" }}
                      disabled={item.quota === 0}
                    >
                      {isChinese ? item.time_slot_hk : item.time_slot_hk_en}
                    </MenuItem>
                  ))}
                </Select>{" "}
                {errors.event_time && (
                  <Typography className="error-text">
                    <Trans i18nKey="form:event_time_error" />
                  </Typography>
                )}
              </>
            );
          }}
        />
      </Box>
      {/* Event Time */}
      <Box className="button-container">
        {" "}
        <LoadingButton
          className="button"
          onClick={() => dispatch(setFormStep({ formKey: "inquiry", step: 1 }))}
        >
          <Trans i18nKey="inquiry:no" />
        </LoadingButton>
        <LoadingButton
          className="loading-button"
          onClick={handleSubmit(handleUpdateClick)}
        >
          <Trans i18nKey="inquiry:yes" />
        </LoadingButton>
      </Box>
      {/* Popup 視窗 */}
      <Popup open={openPopup} modal onClose={() => setOpenPopup(false)}>
        {(close) => (
          <Box className="popup-container">
            <Typography className="popup-title">
              <Trans i18nKey="inquiry:popup_text" />
            </Typography>
            <Box className="popup-button-container">
              <LoadingButton
                className="button"
                onClick={() => {
                  setOpenPopup(false);
                  close();
                }}
                loading={isLoading}
                disabled={isLoading}
              >
                <Trans i18nKey="form:no" />
              </LoadingButton>
              <LoadingButton
                className="loading-button"
                onClick={modifyWorkshop}
                loading={isLoading}
                disabled={isLoading}
              >
                <Trans i18nKey="inquiry:yes" />
              </LoadingButton>
            </Box>
          </Box>
        )}
      </Popup>
    </Box>
  );
};

export default WorkshopChangePage;
